import { CommonModule } from '@angular/common';
import { Component, computed, HostListener, input, output, model, signal, TemplateRef } from '@angular/core';
import { MatButtonModule, MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SafeHtmlPipe } from '@sia/shared';
import { ExcelPreview } from '../../interfaces/file-preview.interface';

@Component({
  selector: 'sia-file-preview-excel',
  host: {
    class: 'block h-full w-full',
  },
  imports: [CommonModule, MatButtonModule, MatIconModule, MatIconButton, MatMiniFabButton, SafeHtmlPipe],
  templateUrl: './file-preview-excel.component.html',
  styleUrl: './file-preview-excel.component.css',
})
export class FilePreviewExcelComponent {
  sheets = input<ExcelPreview>({});
  fullscreen = model<boolean>(false);
  actionsTemplate = input<TemplateRef<any>>();
  sheetChanged = output<void>();

  currentSheetIndex = signal(0);
  zoomLevel = signal(1);

  zoomIn() {
    this.zoomLevel.update((level) => Math.min(level + 0.25, 3));
  }

  zoomOut() {
    this.zoomLevel.update((level) => Math.max(level - 0.25, 0.5));
  }

  resetZoom() {
    this.zoomLevel.set(1);
  }

  onWheel(event: WheelEvent) {
    if (event.ctrlKey) {
      event.preventDefault();
      if (event.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    }
  }

  currentSheet = computed(() => {
    const allSheets = this.sheets().sheets ?? [];
    const index = this.currentSheetIndex();
    return allSheets[index] || null;
  });

  currentSheetName = computed(() => {
    const sheet = this.currentSheet();
    return sheet?.name || `Foglio ${this.currentSheetIndex() + 1}`;
  });

  currentSheetHtml = computed(() => {
    const sheet = this.currentSheet() ?? {};
    return sheet?.pageHtml || '';
  });

  hasMultipleSheets = computed(() => this.sheets().sheets && this.sheets().sheets!.length > 1);
  hasPreviousSheet = computed(() => this.currentSheetIndex() > 0);
  hasNextSheet = computed(() => this.sheets().sheets && this.currentSheetIndex() < this.sheets().sheets!.length - 1);

  closeGallery() {
    this.fullscreen.set(false);
    document.body.style.overflow = 'auto';
  }

  nextSheet() {
    if (this.hasNextSheet()) {
      this.currentSheetIndex.update((index) => index + 1);
      this.sheetChanged.emit();
    }
  }

  previousSheet() {
    if (this.hasPreviousSheet()) {
      this.currentSheetIndex.update((index) => index - 1);
      this.sheetChanged.emit();
    }
  }

  goToSheet(index: number) {
    if (this.sheets().sheets && index >= 0 && index < this.sheets().sheets!.length) {
      this.currentSheetIndex.set(index);
      this.sheetChanged.emit();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (!this.fullscreen()) return;

    switch (event.key) {
      case 'Escape':
        this.closeGallery();
        break;
      case 'ArrowLeft':
        this.previousSheet();
        break;
      case 'ArrowRight':
        this.nextSheet();
        break;
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case '0':
        this.resetZoom();
        break;
    }
  }
}
